import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import TasksPanel from "../components/TasksPanel";
import LeadReferralsSection from "../components/LeadReferralsSection";
import ClientImportWizard from "../components/ClientImportWizard";
import ActivityTimeline from "../components/ActivityTimeline";
import VoiPanel from "../components/VoiPanel";
import { Plus, X, Search, Sparkles, Loader2, Send, Copy, Mail, Trash2, MessageSquare, GitBranch, ChevronDown, Upload, Pin, Lock, Eye, EyeOff, Save, Combine, Phone, MessageCircle, ArrowDownLeft, ArrowUpRight, CheckSquare, Share2, Activity, FileText, Receipt, Clock, PenLine, StickyNote, Calendar, Users } from "lucide-react";
import EmailStatusBadge from "../components/EmailStatusBadge";
import IdVerifiedBadge from "../components/IdVerifiedBadge";
import PortalPasswordDialog from "../components/PortalPasswordDialog";
import IdCheckBulkBar from "../components/IdCheckBulkBar";
import ComposeEmailDialog from "../components/ComposeEmailDialog";
import AssigneePicker, { useTeamMembers } from "../components/AssigneePicker";

const STATUS_OPTIONS = ["active", "inactive", "prospect"];

// v68.7 — Australian entity types. Fetched once from GET /clients/entity-types
// and cached at module scope so every render reuses the same list. Falls back
// to a minimal set if the fetch fails so the UI never blocks on the network.
const FALLBACK_ENTITY_TYPES = [
  { id: "individual", label: "Individual", group: "Person" },
  { id: "business", label: "Business (legacy)", group: "Legacy" },
];
let ENTITY_TYPES_CACHE = null;
let ENTITY_TYPES_PROMISE = null;

function useEntityTypes() {
  const [types, setTypes] = useState(ENTITY_TYPES_CACHE || FALLBACK_ENTITY_TYPES);
  useEffect(() => {
    if (ENTITY_TYPES_CACHE) { setTypes(ENTITY_TYPES_CACHE); return; }
    if (!ENTITY_TYPES_PROMISE) {
      ENTITY_TYPES_PROMISE = axios.get(`${API_BASE}/clients/entity-types`)
        .then((r) => { ENTITY_TYPES_CACHE = r.data?.types || FALLBACK_ENTITY_TYPES; return ENTITY_TYPES_CACHE; })
        .catch(() => FALLBACK_ENTITY_TYPES);
    }
    ENTITY_TYPES_PROMISE.then((list) => setTypes(list));
  }, []);
  return types;
}

// O(1) lookup: entity id → human label. Uses cache when populated.
function entityLabel(id) {
  const list = ENTITY_TYPES_CACHE || FALLBACK_ENTITY_TYPES;
  const hit = list.find((t) => t.id === id);
  return hit ? hit.label : (id || "—");
}

function ClientRow({ c, askingId, invitingId, onInvite, onAskHealth, onCompose, onAssign, onOpen, mergeSel, onToggleMerge, onPassword }) {
  const inMerge = Array.isArray(mergeSel);
  const selected = inMerge && mergeSel.includes(c.client_id);
  return (
    <tr data-testid={`client-row-${c.client_id}`}>
      {inMerge && (
        <td className="w-6">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleMerge(c.client_id)}
            data-testid={`client-merge-select-${c.client_id}`}
            aria-label={`Select ${c.name} for merge`}
          />
        </td>
      )}
      <td>
        <button
          onClick={() => onOpen(c)}
          className="text-[var(--ink)] font-medium hover:underline text-left"
          data-testid={`client-open-${c.client_id}`}
        >
          {c.name}
        </button>
        <span className="ml-2 align-middle"><IdVerifiedBadge client={c} testid={`client-id-verified-${c.client_id}`} /></span>
        {c.voi_required && c.voi_status !== "APPROVED" && (
          <span className="ml-2 align-middle text-[10px] uppercase tracking-wider text-[#B45309]" data-testid={`client-id-flagged-${c.client_id}`}>ID check needed</span>
        )}
      </td>
      <td><span className="badge badge-outline" data-testid={`client-type-badge-${c.client_id}`}>{entityLabel(c.type)}</span></td>
      <td className="font-mono text-[12px]">{c.primary_contact_email || "—"}</td>
      <td>
        <span className="badge" style={{
          background: c.status === "active" ? "var(--moss)" : c.status === "prospect" ? "var(--accent)" : "var(--ink-3)",
          color: "var(--paper)",
        }}>{c.status}</span>
      </td>
      <td>
        <AssigneePicker
          value={c.assignee_id}
          onChange={(uid) => onAssign(c, uid)}
          testid={`client-assignee-${c.client_id}`}
          compact
          label="Assign"
        />
      </td>
      <td>
        {c.portal_user ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-mono text-[var(--ink-2)] truncate" data-testid={`client-portal-user-${c.client_id}`}>{c.portal_user.email}</span>
            <EmailStatusBadge status={c.last_invite_status} testid={`client-invite-status-${c.client_id}`} />
          </div>
        ) : (
          <span className="text-[11px] text-[var(--ink-3)] font-mono" data-testid={`client-portal-none-${c.client_id}`}>not invited</span>
        )}
      </td>
      <td className="text-right">
        <div className="flex flex-wrap justify-end gap-x-3 gap-y-1 max-w-[220px] ml-auto">
          <button
            onClick={() => onCompose(c)}
            disabled={!c.primary_contact_email}
            data-testid={`client-compose-${c.client_id}`}
            className="text-[11px] inline-flex items-center gap-1 text-[var(--ink-2)] hover:text-[var(--ink)] disabled:opacity-40"
            title={c.primary_contact_email ? `Email ${c.primary_contact_email}` : "Add a primary email to compose"}
          >
            <Mail size={11} /> Email
          </button>
          <button
            onClick={() => onInvite(c)}
            disabled={invitingId === c.client_id}
            data-testid={`client-invite-${c.client_id}`}
            className="text-[11px] inline-flex items-center gap-1 text-[var(--ink-2)] hover:text-[var(--ink)] disabled:opacity-50"
            title={c.portal_user ? `Resend invite to ${c.portal_user.email}` : "Invite to client portal"}
          >
            {invitingId === c.client_id ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            {c.portal_user ? "Resend" : "Invite"}
          </button>
          {c.portal_user?.client_user_id && (
            <button onClick={() => onPassword(c)} data-testid={`client-portal-password-${c.client_id}`}
                    className="text-[11px] inline-flex items-center gap-1 text-[var(--ink-2)] hover:text-[var(--ink)]"
                    title="Reset this client's portal password">
              <Lock size={11} /> Password
            </button>
          )}
          <button
            onClick={() => onAskHealth(c.client_id)}
            disabled={askingId === c.client_id}
            data-testid={`client-ask-ai-${c.client_id}`}
            className="text-[11px] inline-flex items-center gap-1 text-[var(--accent)] hover:underline disabled:opacity-50"
          >
            {askingId === c.client_id ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
            {askingId === c.client_id ? "Asking…" : "Ask AI"}
          </button>
        </div>
      </td>
    </tr>
  );
}

function InviteResultModal({ result, onClose, onCopyLink }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(26,24,20,0.45)" }} data-testid="invite-result-modal">
      <div className="bg-[var(--paper)] border border-[var(--border)] rounded-md max-w-lg w-full p-8">
        <div className="overline mb-1">Portal invite</div>
        <h3 className="font-heading text-2xl mb-4" style={{ fontWeight: 500 }}>{result.client_name}</h3>
        <div className="card p-4 mb-4" style={{ background: "var(--subtle)" }}>
          <div className="overline mb-1">Magic link · valid 24h</div>
          <div className="text-[12px] font-mono break-all" data-testid="invite-result-magic-link">
            {result.link?.startsWith("http") ? result.link : window.location.origin + result.link}
          </div>
          {result.email_result?.ok === false && (
            <div className="text-[12px] text-[var(--accent)] mt-2">
              Email delivery failed (Resend test mode delivers only to the verified address).
              Copy the link and send it manually.
            </div>
          )}
          {result.email_result?.ok === true && (
            <div className="text-[12px] text-[var(--moss)] mt-2">Emailed to client.</div>
          )}
        </div>
        <div className="flex gap-3">
          <button onClick={onCopyLink} className="btn-secondary flex-1 justify-center" data-testid="invite-copy-link">
            <Copy size={12} /> Copy link
          </button>
          <button onClick={onClose} className="btn-primary flex-1 justify-center" data-testid="invite-done">Done</button>
        </div>
      </div>
    </div>
  );
}

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [askingId, setAskingId] = useState(null);
  const [invitingId, setInvitingId] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [composeFor, setComposeFor] = useState(null);
  const [passwordFor, setPasswordFor] = useState(null);
  const [myWork, setMyWork] = useState(false);
  // v68.27 — merge selection mode: [] = off, otherwise the selected client_ids.
  const [mergeSel, setMergeSel] = useState(null);
  const [idMode, setIdMode] = useState(() => new URLSearchParams(window.location.search).get("id_filter") === "1");
  const [idSel, setIdSel] = useState([]);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [openClient, setOpenClient] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("client") ? { client_id: p.get("client"), tab: p.get("tab") || undefined } : null;
  });
  const navigate = useNavigate();
  const members = useTeamMembers();

  const reload = async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (myWork) qs.set("assignee_id", "me");
    if (debouncedQ) qs.set("q", debouncedQ);
    const url = `${API_BASE}/clients${qs.toString() ? `?${qs.toString()}` : ""}`;
    const { data } = await axios.get(url);
    setRows(data);
    setLoading(false);
  };

  // Debounce the search box so we hit the API once the user pauses typing.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [myWork, debouncedQ]);

  const assignClient = async (client, uid) => {
    try {
      await axios.patch(`${API_BASE}/clients/${client.client_id}`, { assignee_id: uid });
      setRows((prev) => prev.map((c) =>
        c.client_id === client.client_id ? { ...c, assignee_id: uid } : c,
      ));
      const target = members.find((m) => m.user_id === uid);
      toast.success(uid ? `Assigned to ${target?.name || "teammate"}` : "Unassigned");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const askHealth = async (client_id) => {
    setAskingId(client_id);
    try {
      await axios.post(`${API_BASE}/insights/client-health/${client_id}`);
      toast.success("Client health snapshot drafted", {
        description: "View it in Insights.",
        action: { label: "Open", onClick: () => navigate("/insights") },
      });
      window.dispatchEvent(new CustomEvent("insights:refresh-pending"));
    } catch (err) {
      toast.error(formatApiError(err) || "AI client health failed");
    } finally {
      setAskingId(null);
    }
  };

  const invite = async (client) => {
    // If a portal_user already exists, fast-path through the resend endpoint.
    if (client.portal_user?.email) {
      setInvitingId(client.client_id);
      try {
        const r = await axios.post(`${API_BASE}/clients/${client.client_id}/invite/resend`);
        setInviteResult({ client_name: client.name, ...r.data });
        toast.success(r.data?.email_result?.ok ? "Invite re-emailed" : "Magic link re-issued — copy below if email failed");
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setInvitingId(null);
      }
      return;
    }
    const email = client.primary_contact_email
      || window.prompt(`Invite ${client.name} to client portal — what's their email?`, "");
    if (!email) return;
    setInvitingId(client.client_id);
    try {
      const r = await axios.post(`${API_BASE}/clients/${client.client_id}/invite`, { email, name: client.name });
      setInviteResult({ client_name: client.name, ...r.data });
      toast.success(r.data?.email_result?.ok ? "Invite emailed" : "Magic link created — copy below if email failed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setInvitingId(null);
    }
  };

  const copyInviteLink = async () => {
    const link = inviteResult?.link;
    if (!link) return;
    const full = link.startsWith("http") ? link : window.location.origin + link;
    try { await navigator.clipboard.writeText(full); toast.success("Link copied"); }
    catch { toast.error("Copy failed: " + full); }
  };

  // Server already applies the search (name/email/mobile, decrypted). Keep a
  // light client-side pass only while the debounce catches up.
  const filtered = idMode ? rows.filter((c) => c.voi_status !== "APPROVED") : rows;
  const selMode = Array.isArray(mergeSel) || idMode;
  const rowSel = Array.isArray(mergeSel) ? mergeSel : (idMode ? idSel : null);

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline mb-2">CRM</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>Clients</h1>
        </div>
        <div className="flex gap-3 items-center">
          <button
            onClick={() => setMyWork((v) => !v)}
            data-testid="clients-my-work-toggle"
            className={`text-[11px] inline-flex items-center gap-1 rounded-full px-3 py-1.5 transition ${
              myWork
                ? "bg-[var(--ink)] text-[var(--paper)] border border-[var(--ink)]"
                : "border border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"
            }`}
          >
            My clients {myWork && <span className="opacity-70">·on</span>}
          </button>
          <button
            onClick={() => { setIdMode((v) => !v); setIdSel([]); setMergeSel(null); }}
            data-testid="clients-id-filter-toggle"
            className={`text-[11px] inline-flex items-center gap-1 rounded-full px-3 py-1.5 transition ${
              idMode ? "bg-[var(--ink)] text-[var(--paper)] border border-[var(--ink)]"
                : "border border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"}`}
            title="Show clients who haven't been ID verified — select them to send checks"
          >
            <Lock size={11} /> Not ID verified {idMode && <span className="opacity-70">·on</span>}
          </button>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email or mobile…"
              data-testid="clients-search"
              className="w-72 bg-[var(--paper)] border border-[var(--border)] rounded-full pl-9 pr-4 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]"
            />
          </div>
          <button
            onClick={() => { setIdMode(false); setMergeSel((cur) => cur === null ? [] : null); }}
            data-testid="clients-merge-toggle"
            className={`text-[11px] inline-flex items-center gap-1 rounded-full px-3 py-1.5 transition ${
              Array.isArray(mergeSel)
                ? "bg-[var(--accent)] text-[var(--paper)] border border-[var(--accent)]"
                : "border border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--accent)]"
            }`}
            title="Select 2+ clients to fold duplicates into one"
          >
            <Combine size={12} /> {Array.isArray(mergeSel) ? "Cancel merge" : "Merge duplicates"}
          </button>
          <button onClick={() => setShowImport(true)} className="btn-secondary" data-testid="client-import-btn">
            <Upload size={14} /> Import CSV
          </button>
          <button onClick={() => setShowModal(true)} className="btn-primary" data-testid="client-create-btn">
            <Plus size={14} /> New client
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[var(--ink-3)]">Loading…</div>
      ) : (
        <div className="card overflow-hidden">
          {idMode && (
            <IdCheckBulkBar selected={idSel} visibleIds={filtered.map((c) => c.client_id)}
              onSelectAll={setIdSel} onDone={() => { setIdSel([]); reload(); }} />
          )}
          {Array.isArray(mergeSel) && (
            <MergeToolbar
              mergeSel={mergeSel}
              busy={mergeBusy}
              onCancel={() => setMergeSel(null)}
              onMerge={async (survivorId) => {
                const dupes = mergeSel.filter((id) => id !== survivorId);
                if (!dupes.length) return;
                setMergeBusy(true);
                try {
                  const { data } = await axios.post(`${API_BASE}/clients/${survivorId}/merge`,
                    { duplicate_ids: dupes });
                  const moved = Object.values(data.moved_counts || {}).reduce((a, b) => a + b, 0);
                  toast.success(`Merged ${dupes.length} client${dupes.length > 1 ? "s" : ""} — moved ${moved} records`);
                  setMergeSel(null);
                  reload();
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setMergeBusy(false); }
              }}
              clients={rows.filter((c) => mergeSel.includes(c.client_id))}
            />
          )}
          <table className="report-table">
            <thead>
              <tr>
                {selMode && <th style={{ width: 28 }}></th>}
                <th>Name</th><th>Type</th><th>Primary Contact</th><th>Status</th><th>Assignee</th><th>Portal</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <ClientRow
                  key={c.client_id}
                  c={c}
                  askingId={askingId}
                  invitingId={invitingId}
                  onInvite={invite}
                  onAskHealth={askHealth}
                  onCompose={setComposeFor}
                  onAssign={assignClient}
                  onPassword={setPasswordFor}
                  onOpen={Array.isArray(mergeSel) ? () => {} : setOpenClient}
                  mergeSel={rowSel}
                  onToggleMerge={(id) => (Array.isArray(mergeSel) ? setMergeSel : setIdSel)((cur) => cur.includes(id)
                    ? cur.filter((x) => x !== id)
                    : [...cur, id])}
                />
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={selMode ? 8 : 7} className="text-center text-[var(--ink-3)] py-10">No clients match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <ClientModal onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); reload(); }} />}
      <ClientImportWizard
        open={showImport}
        onClose={() => { setShowImport(false); reload(); }}
        onImported={() => { reload(); }}
      />
      {passwordFor && <PortalPasswordDialog client={passwordFor} onClose={() => { setPasswordFor(null); reload(); }} />}
      {inviteResult && (
        <InviteResultModal result={inviteResult} onClose={() => setInviteResult(null)} onCopyLink={copyInviteLink} />
      )}
      <ComposeEmailDialog
        open={!!composeFor}
        onClose={() => setComposeFor(null)}
        onSent={() => setComposeFor(null)}
        context={{ client_id: composeFor?.client_id }}
        defaultTo={composeFor?.primary_contact_email || ""}
        defaultToName={composeFor?.name || ""}
      />
      {openClient && (
        <ClientDetailDrawer
          clientId={openClient.client_id}
          initialTab={openClient.tab}
          allClients={rows}
          onClose={() => setOpenClient(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

function ClientModal({ onClose, onCreated }) {
  const entityTypes = useEntityTypes();
  const [form, setForm] = useState({
    name: "", type: "individual", primary_contact_email: "",
    primary_contact_phone: "", status: "active", notes: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.primary_contact_email) delete payload.primary_contact_email;
      await axios.post(`${API_BASE}/clients`, payload);
      onCreated();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="client-modal"
         style={{ background: "rgba(26,24,20,0.45)" }}>
      <div className="bg-[var(--paper)] border border-[var(--border)] rounded-md max-w-lg w-full p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline">New client</div>
            <h3 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>Add a client</h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="client-modal-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="client-form">
          <ModalField label="Name" value={form.name} onChange={upd("name")} testid="client-form-name" required />
          <EntityTypeSelect label="Entity type" value={form.type} onChange={upd("type")} types={entityTypes} testid="client-form-type" />
          <ModalField label="Primary email" type="email" value={form.primary_contact_email} onChange={upd("primary_contact_email")} testid="client-form-email" />
          <ModalField label="Phone" value={form.primary_contact_phone} onChange={upd("primary_contact_phone")} testid="client-form-phone" />
          <ModalSelect label="Status" value={form.status} onChange={upd("status")} options={STATUS_OPTIONS} testid="client-form-status" />
          {error && <div className="text-[13px] text-[var(--accent)]" data-testid="client-form-error">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center" data-testid="client-form-submit">
              {saving ? "Saving…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalField({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
    </div>
  );
}
function ModalSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

// v68.7 — Entity type picker with <optgroup>s (Person / Company / Trust /
// Super / NFP / Other / Legacy). `types` shape: [{id,label,group,code}, ...].
function EntityTypeSelect({ label, value, onChange, types, testid }) {
  const grouped = {};
  const order = [];
  for (const t of types) {
    if (!grouped[t.group]) { grouped[t.group] = []; order.push(t.group); }
    grouped[t.group].push(t);
  }
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]">
        {order.map((g) => (
          <optgroup key={g} label={g}>
            {grouped[g].map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}

function ClientDetailDrawer({ clientId, allClients, onClose, onChanged, initialTab }) {
  const [tab, setTab] = useState(initialTab || "notes");
  const [client, setClient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [rels, setRels] = useState([]);
  const [relTypes, setRelTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newRelClient, setNewRelClient] = useState("");
  const [newRelType, setNewRelType] = useState("");
  const entityTypes = useEntityTypes();

  const changeType = async (nextType) => {
    if (!client || nextType === client.type) return;
    try {
      const r = await axios.patch(`${API_BASE}/clients/${clientId}`, { type: nextType });
      setClient(r.data);
      toast.success(`Type set to ${entityLabel(nextType)}`);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reload = async () => {
    try {
      // allSettled — one soft failure (e.g. relationships 4xx) shouldn't blank the whole drawer
      const [c, n, r, t, s] = await Promise.allSettled([
        axios.get(`${API_BASE}/clients/${clientId}`),
        axios.get(`${API_BASE}/clients/${clientId}/notes`),
        axios.get(`${API_BASE}/clients/${clientId}/relationships`),
        axios.get(`${API_BASE}/clients/relationship-types`),
        axios.get(`${API_BASE}/firm-services`),
      ]);
      if (c.status === "fulfilled") setClient(c.value.data);
      else throw c.reason;
      setNotes(n.status === "fulfilled" ? n.value.data || [] : []);
      setRels(r.status === "fulfilled" ? r.value.data || [] : []);
      setRelTypes(t.status === "fulfilled" ? t.value.data || [] : []);
      setServices(s.status === "fulfilled" ? s.value.data || [] : []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const addNote = async () => {
    const txt = newNote.trim();
    if (!txt) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/clients/${clientId}/notes`, { text: txt });
      setNewNote("");
      await reload();
      toast.success("Note added");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API_BASE}/clients/${clientId}/notes/${id}`);
      await reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addRel = async () => {
    if (!newRelClient || !newRelType) {
      toast.error("Pick a client and a relationship type");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/clients/${clientId}/relationships`, {
        related_client_id: newRelClient,
        relationship_type: newRelType,
      });
      setNewRelClient(""); setNewRelType(""); setAdding(false);
      await reload();
      onChanged?.();
      toast.success("Relationship added");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const deleteRel = async (rid) => {
    if (!window.confirm("Remove this relationship?")) return;
    try {
      await axios.delete(`${API_BASE}/clients/${clientId}/relationships/${rid}`);
      await reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!client) {
    return (
      <div
        className="fixed inset-0 z-[60] bg-[var(--ink)]/40 flex items-start justify-end"
        onClick={onClose}
        data-testid="client-detail-drawer"
      >
        <div className="bg-[var(--paper)] w-[640px] h-full p-6 border-l border-[var(--border)]"
              onClick={(e) => e.stopPropagation()}>
          <Loader2 className="animate-spin" size={20}/>
          <div className="text-[12px] text-[var(--ink-3)] mt-2">Loading client…</div>
        </div>
      </div>
    );
  }

  const otherClients = allClients.filter((c) => c.client_id !== clientId);

  return (
    <div
      className="fixed inset-0 z-[60] bg-[var(--ink)]/40 flex items-start justify-end"
      onClick={onClose}
      data-testid="client-detail-drawer"
    >
      <div
        className="bg-[var(--paper)] w-full max-w-[640px] h-full overflow-y-auto border-l border-[var(--border)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[var(--paper)] border-b border-[var(--border)] p-5 flex items-start justify-between z-10">
          <div>
            <h2 className="font-heading text-2xl" style={{ fontWeight: 500 }} data-testid="client-detail-name">{client.name}</h2>
            <div className="text-[11px] text-[var(--ink-3)] font-mono mt-1">{client.client_id}</div>
            <div className="text-[12px] text-[var(--ink-2)] mt-1 flex items-center gap-2 flex-wrap">
              <span className="badge badge-outline" data-testid="client-detail-type-badge">{entityLabel(client.type)}</span>
              <select
                value={client.type}
                onChange={(e) => changeType(e.target.value)}
                data-testid="client-detail-type-select"
                className="text-[11px] bg-transparent border border-[var(--border)] rounded px-1.5 py-0.5 focus:outline-none focus:border-[var(--ink)]"
                title="Change entity type"
              >
                {entityTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              <span className="text-[var(--ink-3)]">·</span>
              <span>{client.primary_contact_email || "—"}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="client-detail-close">
            <X size={18}/>
          </button>
        </div>

        <div className="px-5 pt-4 pb-2 border-b border-[var(--border)]" data-testid="client-detail-services">
          <div className="flex items-center justify-between mb-1.5">
            <div className="overline text-[10px]">Services</div>
            <a href="/settings/services" className="text-[10px] text-[var(--ink-3)] hover:text-[var(--accent)]"
               data-testid="client-detail-manage-services-link">Manage →</a>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {services.length === 0 && (
              <div className="text-[11px] text-[var(--ink-3)]">
                No services configured — <a href="/settings/services" className="underline">add some</a>.
              </div>
            )}
            {services.map((s) => {
              const on = (client.service_ids || []).includes(s.service_id);
              return (
                <button key={s.service_id} onClick={async () => {
                  const next = on
                    ? (client.service_ids || []).filter((x) => x !== s.service_id)
                    : [...(client.service_ids || []), s.service_id];
                  try {
                    const r = await axios.patch(`${API_BASE}/clients/${clientId}`, { service_ids: next });
                    setClient(r.data); toast.success("Updated"); onChanged?.();
                  } catch (err) { toast.error(formatApiError(err)); }
                }} title={s.description || s.name}
                  data-testid={`client-detail-service-${s.service_id}`}
                  className={`text-[11px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${
                    on ? "bg-[var(--accent)] text-[var(--paper)] border-[var(--accent)]" : "border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--accent)]"
                  }`}>
                  {s.code && <span className="font-mono text-[9px] opacity-80">{s.code}</span>}
                  {s.name}
                </button>
              );
            })}
          </div>
        </div>

        <DuplicateBanner clientId={clientId} onMerged={() => { onChanged && onChanged(); onClose && onClose(); }} />

        <div className="px-5 pt-3 flex gap-1 border-b border-[var(--border)]">
          <button
            onClick={() => setTab("notes")}
            data-testid="client-tab-notes"
            className={`text-[11px] uppercase tracking-wider px-3 py-2 border-b-2 ${tab === "notes" ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"}`}
          >
            <MessageSquare size={11} className="inline mr-1" /> Notes ({notes.length})
          </button>
          <button
            onClick={() => setTab("tasks")}
            data-testid="client-tab-tasks"
            className={`text-[11px] uppercase tracking-wider px-3 py-2 border-b-2 ${tab === "tasks" ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"}`}
          >
            Tasks
          </button>
          <button
            onClick={() => setTab("relationships")}
            data-testid="client-tab-relationships"
            className={`text-[11px] uppercase tracking-wider px-3 py-2 border-b-2 ${tab === "relationships" ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"}`}
          >
            <GitBranch size={11} className="inline mr-1" /> Relationships ({rels.length})
          </button>
          <button
            onClick={() => setTab("activity")}
            data-testid="client-tab-activity"
            className={`text-[11px] uppercase tracking-wider px-3 py-2 border-b-2 ${tab === "activity" ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"}`}
          >
            <Activity size={11} className="inline mr-1" /> Activity
          </button>
          <button
            onClick={() => setTab("voi")}
            data-testid="client-tab-voi"
            className={`text-[11px] uppercase tracking-wider px-3 py-2 border-b-2 ${tab === "voi" ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"}`}
          >
            <Lock size={11} className="inline mr-1" /> ID check
          </button>
          {client?.origin_lead_id && (
            <button
              onClick={() => setTab("referrals")}
              data-testid="client-tab-referrals"
              className={`text-[11px] uppercase tracking-wider px-3 py-2 border-b-2 ${tab === "referrals" ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"}`}
            >
              <Share2 size={11} className="inline mr-1" /> Referrals
            </button>
          )}
          <button
            onClick={() => setTab("sensitive")}
            data-testid="client-tab-sensitive"
            className={`text-[11px] uppercase tracking-wider px-3 py-2 border-b-2 ${tab === "sensitive" ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"}`}
          >
            <Lock size={11} className="inline mr-1" /> Sensitive
          </button>
        </div>

        {tab === "activity" && (
          <ActivityTimeline scope="client" id={clientId} />
        )}

        {tab === "voi" && <VoiPanel clientId={clientId} client={client} />}

        {tab === "referrals" && client?.origin_lead_id && (
          <div className="p-5">
            <LeadReferralsSection leadId={client.origin_lead_id} />
          </div>
        )}

        {tab === "sensitive" && (
          <SensitivePiiPanel clientId={clientId} client={client} onChanged={reload}/>
        )}

        {tab === "tasks" && (
          <div className="p-5">
            <TasksPanel parentKind="client" parentId={clientId} />
          </div>
        )}

        {tab === "notes" && (
          <div className="p-5 space-y-3">
            <div className="flex gap-2">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note about this client…"
                rows={2}
                className="flex-1 bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]"
                data-testid="client-note-input"
              />
              <button onClick={addNote} disabled={busy || !newNote.trim()} className="btn-primary self-stretch" data-testid="client-note-add">
                {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
                Add
              </button>
            </div>
            {notes.length === 0 ? (
              <div className="text-[12px] text-[var(--ink-3)] italic py-4 text-center">
                No notes yet — capture context, meetings, follow-ups.
              </div>
            ) : (
              <ul className="space-y-2">
                {notes.map((n) => (
                  <li key={n.note_id}
                      className={`card p-3 ${n.is_pinned ? "border-[var(--accent)] bg-[var(--accent-light)]" : ""}`}
                      data-testid={`client-note-${n.note_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[13px] text-[var(--ink)] whitespace-pre-wrap flex-1">{n.text}</div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(`${API_BASE}/clients/${clientId}/notes/${n.note_id}/pin?pinned=${!n.is_pinned}`);
                              reload();
                            } catch (e) { toast.error(formatApiError(e)); }
                          }}
                          className={`flex-shrink-0 ${n.is_pinned ? "text-[var(--accent)]" : "text-[var(--ink-3)] hover:text-[var(--accent)]"}`}
                          data-testid={`client-note-pin-${n.note_id}`}
                          title={n.is_pinned ? "Unpin note" : "Pin note to top"}>
                          <Pin size={12} className={n.is_pinned ? "fill-current" : ""}/>
                        </button>
                        <button onClick={() => deleteNote(n.note_id)}
                                className="text-[var(--ink-3)] hover:text-[#B91C1C]"
                                data-testid={`client-note-delete-${n.note_id}`}
                                title="Delete note">
                          <Trash2 size={12}/>
                        </button>
                      </div>
                    </div>
                    <div className="text-[10px] text-[var(--ink-3)] font-mono mt-1.5 flex items-center gap-1.5">
                      {n.is_pinned && <span className="text-[var(--accent)] font-semibold">PINNED</span>}
                      <span>{n.author_name} · {new Date(n.created_at).toLocaleString()}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "relationships" && (
          <div className="p-5 space-y-3">
            {adding ? (
              <div className="card p-3 space-y-2 bg-[var(--subtle)]" data-testid="client-rel-add-form">
                <div className="overline text-[10px]">Link to another client</div>
                <select
                  value={newRelClient}
                  onChange={(e) => setNewRelClient(e.target.value)}
                  className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]"
                  data-testid="client-rel-target-select"
                >
                  <option value="">— Pick a client —</option>
                  {otherClients.map((c) => (
                    <option key={c.client_id} value={c.client_id}>{c.name}</option>
                  ))}
                </select>
                <select
                  value={newRelType}
                  onChange={(e) => setNewRelType(e.target.value)}
                  className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]"
                  data-testid="client-rel-type-select"
                >
                  <option value="">— Relationship type —</option>
                  {relTypes.map((t) => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <button onClick={addRel} disabled={busy} className="btn-primary flex-1" data-testid="client-rel-add-submit">
                    {busy ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13}/>} Add link
                  </button>
                  <button onClick={() => { setAdding(false); setNewRelClient(""); setNewRelType(""); }} className="btn-secondary">
                    <X size={13}/>
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAdding(true)} className="btn-secondary" data-testid="client-rel-add-btn">
                <Plus size={13}/> Link to another client
              </button>
            )}
            {rels.length === 0 ? (
              <div className="text-[12px] text-[var(--ink-3)] italic py-4 text-center">
                No linked clients yet. Add spouses, business partners, parent companies.
              </div>
            ) : (
              <ul className="space-y-2">
                {rels.map((r) => (
                  <li key={r.relationship_id} className="card p-3" data-testid={`client-rel-${r.relationship_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
                          {r.relationship_label}
                        </div>
                        <div className="text-[14px] text-[var(--ink)] font-medium">
                          {r.related?.name || r.related_client_id}
                        </div>
                        <div className="text-[10px] font-mono text-[var(--ink-3)]">
                          {r.related?.primary_contact_email || ""}
                        </div>
                      </div>
                      <button onClick={() => deleteRel(r.relationship_id)}
                              className="text-[var(--ink-3)] hover:text-[#B91C1C]"
                              data-testid={`client-rel-delete-${r.relationship_id}`}
                              title="Remove link">
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}



function SensitivePiiPanel({ clientId, client, onChanged }) {
  const { state } = useAuth();
  const isAdmin = ["owner", "manager"].includes(state?.user?.role);
  const [revealed, setRevealed] = useState({});
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  if (!client) {
    return <div className="p-5 text-[12px] text-[var(--ink-3)]">Loading…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="p-5">
        <div className="card p-4 text-[12.5px] text-[var(--ink-2)] inline-flex items-start gap-2">
          <Lock size={14} className="mt-0.5 text-[var(--ink-3)]"/>
          <div>
            <div className="font-medium mb-0.5">Sensitive fields are owner/manager only.</div>
            <div className="text-[var(--ink-3)]">Your role ({state?.user?.role || "staff"}) cannot view or edit TFN, BSB, account number, DOB, or address.</div>
          </div>
        </div>
      </div>
    );
  }

  const FIELDS = [
    { key: "tfn", label: "Tax File Number", format: "9-digit" },
    { key: "bank_bsb", label: "BSB", format: "6-digit" },
    { key: "bank_account_number", label: "Account number" },
    { key: "dob", label: "Date of birth", format: "YYYY-MM-DD" },
    { key: "address", label: "Address", multiline: true },
  ];

  const reveal = async (field) => {
    try {
      const r = await axios.get(`${API_BASE}/clients/${clientId}/pii/reveal?fields=${field}`);
      setRevealed((prev) => ({ ...prev, [field]: r.data.revealed[field] || "" }));
      toast(`${field} revealed — this view was logged to audit.`, { duration: 2200 });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const conceal = (field) => {
    setRevealed((prev) => { const out = { ...prev }; delete out[field]; return out; });
  };
  const startEdit = async () => {
    try {
      const r = await axios.get(`${API_BASE}/clients/${clientId}/pii/reveal`);
      setDraft({ ...r.data.revealed });
      setRevealed({ ...r.data.revealed });
      setEditing(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API_BASE}/clients/${clientId}`, draft);
      toast.success("Sensitive fields saved (encrypted at rest)");
      setEditing(false);
      setRevealed({});
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 space-y-3" data-testid="sensitive-panel">
      <div className="text-[11px] text-[var(--ink-3)] inline-flex items-center gap-1.5">
        <Lock size={11}/> Encrypted at rest with Fernet · Every reveal is logged to the audit trail
      </div>
      <div className="space-y-2">
        {FIELDS.map((f) => {
          const masked = client[f.key];
          const plain = revealed[f.key];
          const isRevealed = plain !== undefined;
          return (
            <div key={f.key} className="card p-3" data-testid={`sensitive-row-${f.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mb-1">
                    {f.label}{f.format && <span className="lowercase tracking-normal"> · {f.format}</span>}
                  </div>
                  {editing ? (
                    f.multiline ? (
                      <textarea value={draft[f.key] || ""} rows={2}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                        className="w-full bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[13px] font-mono"
                        data-testid={`sensitive-edit-${f.key}`}/>
                    ) : (
                      <input type="text" value={draft[f.key] || ""}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                        className="w-full bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[13px] font-mono"
                        data-testid={`sensitive-edit-${f.key}`}/>
                    )
                  ) : (
                    <div className="font-mono text-[13.5px] text-[var(--ink)]">
                      {isRevealed ? (
                        <span data-testid={`sensitive-value-${f.key}`}>{plain || <em className="text-[var(--ink-3)]">— empty —</em>}</span>
                      ) : (
                        <span className="text-[var(--ink-2)]" data-testid={`sensitive-masked-${f.key}`}>
                          {masked || <em className="text-[var(--ink-3)]">— not set —</em>}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {!editing && masked && (
                  <button onClick={() => (isRevealed ? conceal(f.key) : reveal(f.key))}
                    className="text-[var(--ink-3)] hover:text-[var(--accent)] p-1 shrink-0"
                    title={isRevealed ? "Hide" : "Reveal (logged to audit)"}
                    data-testid={`sensitive-reveal-${f.key}`}>
                    {isRevealed ? <EyeOff size={13}/> : <Eye size={13}/>}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        {editing ? (
          <>
            <button onClick={() => { setEditing(false); setRevealed({}); }}
                    className="text-[12px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                    data-testid="sensitive-cancel-btn">Cancel</button>
            <button onClick={saveEdit} disabled={busy}
                    className="btn-primary text-[12px] inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="sensitive-save-btn">
              {busy ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>}
              Save (encrypted)
            </button>
          </>
        ) : (
          <button onClick={startEdit} className="btn-primary text-[12px]" data-testid="sensitive-edit-btn">
            Edit sensitive fields
          </button>
        )}
      </div>
    </div>
  );
}


/* ---------------- v68.27 — Merge toolbar + Communications panel ---------------- */

function MergeToolbar({ mergeSel, clients, busy, onCancel, onMerge }) {
  const [survivorId, setSurvivorId] = useState(mergeSel[0] || "");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    if (!mergeSel.includes(survivorId) && mergeSel[0]) setSurvivorId(mergeSel[0]);
  }, [mergeSel, survivorId]);
  // Invalidate any stale preview when the selection or survivor changes.
  useEffect(() => { setPreview(null); }, [mergeSel, survivorId]);

  const canMerge = mergeSel.length >= 2 && survivorId && mergeSel.includes(survivorId);
  const dupes = mergeSel.filter((id) => id !== survivorId);
  const totalMoves = preview
    ? Object.values(preview.moved_counts || {}).reduce((a, b) => a + b, 0)
    : 0;

  const runPreview = async () => {
    if (!canMerge) return;
    setPreviewing(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/clients/${survivorId}/merge?dry_run=true`,
        { duplicate_ids: dupes },
      );
      setPreview(data);
      setConfirming(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPreviewing(false); }
  };

  return (
    <div className="px-4 py-3 border-b border-[var(--border)] bg-[var(--accent-light)]/30 flex items-center gap-3 flex-wrap"
         data-testid="clients-merge-toolbar">
      <div className="text-[12px] text-[var(--ink-2)]">
        <span className="font-medium">{mergeSel.length}</span> selected
        {mergeSel.length < 2 && <span className="text-[var(--ink-3)]"> — pick at least 2</span>}
      </div>
      {mergeSel.length >= 2 && (
        <>
          <div className="text-[11px] text-[var(--ink-3)]">Survivor:</div>
          <select
            value={survivorId}
            onChange={(e) => setSurvivorId(e.target.value)}
            data-testid="clients-merge-survivor"
            className="bg-white border border-[var(--border)] rounded px-2 py-1 text-[12px] focus:outline-none focus:border-[var(--ink)]"
          >
            {clients.map((c) => (
              <option key={c.client_id} value={c.client_id}>
                {c.name} · {c.primary_contact_email || c.client_id.slice(-6)}
              </option>
            ))}
          </select>
          <div className="text-[10px] text-[var(--ink-3)] italic">
            All jobs, invoices, emails, docs, tasks move to the survivor. Others become tombstones.
          </div>
        </>
      )}
      <div className="ml-auto flex items-center gap-2">
        <button onClick={onCancel} disabled={busy}
                data-testid="clients-merge-cancel"
                className="btn-secondary text-[11px]">Cancel</button>
        <button
          onClick={runPreview}
          disabled={!canMerge || busy || previewing}
          data-testid="clients-merge-preview"
          className="btn-secondary text-[11px] disabled:opacity-50"
        >
          {previewing ? <Loader2 size={11} className="animate-spin"/> : <Eye size={11}/>}
          Preview
        </button>
      </div>
      {preview && confirming && (
        <div className="w-full mt-2 rounded-md border border-[var(--border)] bg-white p-3"
             data-testid="clients-merge-preview-panel">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="text-[12px] font-medium text-[var(--ink)]">
                Ready to move {totalMoves} record{totalMoves === 1 ? "" : "s"} from{" "}
                {dupes.length} duplicate{dupes.length === 1 ? "" : "s"} → survivor
              </div>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-[11px]">
                {Object.entries(preview.moved_counts || {})
                  .filter(([, n]) => n > 0)
                  .sort(([, a], [, b]) => b - a)
                  .map(([col, n]) => (
                    <div key={col} className="flex justify-between border-b border-[var(--border)] py-0.5">
                      <span className="capitalize text-[var(--ink-2)]">{col.replace(/_/g, " ")}</span>
                      <span className="font-mono text-[var(--ink)]">{n}</span>
                    </div>
                  ))}
                {totalMoves === 0 && (
                  <div className="col-span-full text-[var(--ink-3)] italic">
                    No child records — just the client rows will collapse.
                  </div>
                )}
              </div>
              {preview.duplicates.some((d) => d.skipped === "already_merged") && (
                <div className="mt-2 text-[10px] text-[var(--ink-3)]">
                  Note: {preview.duplicates.filter((d) => d.skipped === "already_merged").length} already merged and will be skipped.
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              <button
                onClick={() => onMerge(survivorId)}
                disabled={busy}
                data-testid="clients-merge-confirm"
                className="btn-primary text-[11px]"
              >
                {busy ? <Loader2 size={11} className="animate-spin"/> : <Combine size={11}/>}
                Confirm merge
              </button>
              <button
                onClick={() => { setPreview(null); setConfirming(false); }}
                className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                data-testid="clients-merge-back"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientCommsPanel({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [qInput, setQInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  // Debounce keyword search so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(qInput.trim()), 300);
    return () => clearTimeout(id);
  }, [qInput]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const params = debouncedQ ? { q: debouncedQ } : {};
    axios.get(`${API_BASE}/clients/${clientId}/communications`, { params })
      .then(({ data: d }) => { if (!cancel) setData(d); })
      .catch((e) => { if (!cancel) setErr(formatApiError(e)); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [clientId, debouncedQ]);

  const events = data?.events || [];
  const primaryEmail = data?.primary_contact_email;

  const searchBar = (
    <div className="relative mb-3">
      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
      <input
        type="search"
        value={qInput}
        onChange={(e) => setQInput(e.target.value)}
        placeholder="Search subject, body, from, to…"
        data-testid="client-comms-search"
        className="w-full bg-white border border-[var(--border)] rounded pl-8 pr-8 py-1.5 text-[12px] focus:outline-none focus:border-[var(--ink)]"
      />
      {qInput && (
        <button
          onClick={() => setQInput("")}
          data-testid="client-comms-search-clear"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ink-3)] hover:text-[var(--ink)]"
          title="Clear"
        >
          <X size={11} />
        </button>
      )}
    </div>
  );

  if (loading && !data) return (
    <div className="p-5">
      {searchBar}
      <div className="text-[12px] text-[var(--ink-3)] inline-flex items-center gap-2">
        <Loader2 size={12} className="animate-spin" /> Loading communications…
      </div>
    </div>
  );
  if (err) return <div className="p-5 text-[12px] text-[#B91C1C]">{err}</div>;

  const formatWhen = (e) => {
    const iso = e.at_iso || (e.at ? new Date(e.at * 1000).toISOString() : null);
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="p-5" data-testid="client-comms-list">
      {searchBar}
      {events.length === 0 ? (
        <div className="text-[12px] text-[var(--ink-3)] italic">
          {debouncedQ ? (
            <>No results for &ldquo;<span className="font-mono">{debouncedQ}</span>&rdquo;.</>
          ) : (
            <>
              No emails, calls or SMS on this client yet. Everything sent from Practice OS,
              or received from <span className="font-mono">{primaryEmail || "their email"}</span>,
              lands here automatically.
            </>
          )}
        </div>
      ) : (
        <>
          <div className="overline text-[10px] mb-3">
            {events.length} event{events.length > 1 ? "s" : ""}
            {debouncedQ && <span className="text-[var(--ink-3)] normal-case ml-1">· filtered</span>}
          </div>
          <div className="space-y-0">
            {events.map((e, i) => {
              const inbound = e.direction === "inbound";
              const iconProps = { size: 14 };
              const icon = e.kind === "call" ? <Phone {...iconProps}/>
                : e.kind === "sms" ? <MessageCircle {...iconProps}/>
                : inbound ? <ArrowDownLeft {...iconProps}/>
                : <ArrowUpRight {...iconProps}/>;
              return (
                <div
                  key={`${e.kind}-${e.email_id || e.log_id || i}`}
                  className="flex items-start gap-3 py-2.5 border-b border-[var(--border)]"
                  data-testid={`client-comms-event-${i}`}
                >
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 ${
                    inbound ? "bg-[var(--subtle)] text-[var(--ink)]" : "bg-[var(--accent-light)] text-[var(--accent)]"
                  }`}>
                    {icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-[11px] text-[var(--ink-3)]">
                      <span className="uppercase tracking-wider font-medium" style={{
                        color: inbound ? "var(--ink)" : "var(--accent)",
                      }}>
                        {e.kind === "email_log" ? "email · sent" : `${e.kind} · ${e.direction || "outbound"}`}
                      </span>
                      <span>·</span>
                      <span>{formatWhen(e)}</span>
                      {e.kind === "email" && e.reply_channel && (
                        <span className="text-[var(--moss)]">· replied via {e.reply_channel}</span>
                      )}
                      {e.kind === "email_log" && (
                        e.ok
                          ? <span className="text-[var(--moss)] inline-flex items-center gap-0.5"><CheckSquare size={10}/> delivered</span>
                          : <span className="text-[#B91C1C]">· failed{e.error ? `: ${e.error}` : ""}</span>
                      )}
                    </div>
                    <div className="text-[13px] text-[var(--ink)] font-medium mt-0.5 truncate">
                      {e.subject || (e.kind === "call" ? `${e.duration_seconds || 0}s call` : "(no subject)")}
                    </div>
                    {(e.snippet || e.body) && (
                      <div className="text-[11px] text-[var(--ink-2)] mt-1 line-clamp-2">
                        {e.snippet || e.body}
                      </div>
                    )}
                    <div className="text-[10px] text-[var(--ink-3)] font-mono mt-1">
                      {e.kind === "email" && (
                        <>{inbound ? "from " : "to "}{inbound ? e.from : (e.to || []).join(", ")}</>
                      )}
                      {e.kind === "email_log" && <>to {e.to}</>}
                      {e.kind === "sms" && <>{inbound ? "from " : "to "}{inbound ? e.from : e.to}</>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* v68.28 — Duplicate-detection banner. Renders at the top of the client
   drawer when another client shares this one's email or phone. Two-step
   merge: fetch dry-run counts → confirm. Owner-only actions. */
function DuplicateBanner({ clientId, onMerged }) {
  const [dupes, setDupes] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [confirmFor, setConfirmFor] = useState(null);  // {suggestion, preview}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/clients/${clientId}/duplicate-suggestions`)
      .then(({ data }) => { if (!cancel) setDupes(data?.suggestions || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [clientId]);

  const startMerge = async (suggestion) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/clients/${suggestion.client_id}/merge?dry_run=true`,
        { duplicate_ids: [clientId] },
      );
      setConfirmFor({ suggestion, preview: data });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doMerge = async () => {
    setBusy(true);
    try {
      const { suggestion } = confirmFor;
      await axios.post(`${API_BASE}/clients/${suggestion.client_id}/merge`,
                       { duplicate_ids: [clientId] });
      toast.success(`Merged into ${suggestion.name}`);
      onMerged && onMerged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (dismissed || !dupes.length) return null;
  return (
    <div className="mx-5 mt-3 rounded-md border border-[var(--accent)] bg-[var(--accent-light)]/40 p-3 space-y-2"
         data-testid="client-duplicate-banner">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[12px] text-[var(--ink)]">
          <span className="font-medium">Possible duplicate{dupes.length > 1 ? "s" : ""}:</span>{" "}
          {dupes.length} other client{dupes.length > 1 ? "s" : ""} share this contact info.
        </div>
        <button
          onClick={() => setDismissed(true)}
          data-testid="client-duplicate-dismiss"
          className="text-[var(--ink-3)] hover:text-[var(--ink)]"
          title="Dismiss for now"
        ><X size={12}/></button>
      </div>
      <ul className="space-y-1">
        {dupes.map((d) => (
          <li key={d.client_id}
              className="flex items-center gap-2 text-[11px] py-1 rounded px-2 bg-white/70"
              data-testid={`client-duplicate-${d.client_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[var(--ink)] truncate">{d.name}</div>
              <div className="text-[10px] text-[var(--ink-3)] font-mono truncate">
                {d.primary_contact_email}
                {d.primary_contact_phone && <> · {d.primary_contact_phone}</>}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {(d.match_reason || []).map((r) => (
                <span key={r} className="badge text-[9px]" style={{ background: "var(--subtle)" }}>{r}</span>
              ))}
              <button
                onClick={() => startMerge(d)}
                disabled={busy}
                data-testid={`client-duplicate-merge-${d.client_id}`}
                className="btn-secondary text-[10px] py-0.5"
              >
                {busy ? <Loader2 size={10} className="animate-spin"/> : <Combine size={10}/>}
                Merge into this
              </button>
            </div>
          </li>
        ))}
      </ul>
      {confirmFor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[var(--ink)]/40"
             onClick={() => !busy && setConfirmFor(null)}
             data-testid="client-duplicate-confirm">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="overline mb-1">Confirm merge</div>
            <div className="text-[14px] text-[var(--ink)] mb-3">
              Fold this client into <span className="font-medium">{confirmFor.suggestion.name}</span>?
            </div>
            <div className="rounded border border-[var(--border)] p-2.5 space-y-1 mb-3 text-[11px]">
              {Object.entries(confirmFor.preview.moved_counts || {})
                .filter(([, n]) => n > 0)
                .sort(([, a], [, b]) => b - a)
                .map(([col, n]) => (
                  <div key={col} className="flex justify-between">
                    <span className="capitalize text-[var(--ink-2)]">{col.replace(/_/g, " ")}</span>
                    <span className="font-mono">{n}</span>
                  </div>
                ))}
              {!Object.values(confirmFor.preview.moved_counts || {}).some((n) => n > 0) && (
                <div className="text-[var(--ink-3)] italic">
                  No child records to move — the current row will just collapse into the survivor.
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmFor(null)} disabled={busy}
                      className="btn-secondary text-[12px]"
                      data-testid="client-duplicate-confirm-cancel">Cancel</button>
              <button onClick={doMerge} disabled={busy}
                      className="btn-primary text-[12px]"
                      data-testid="client-duplicate-confirm-do">
                {busy ? <Loader2 size={12} className="animate-spin"/> : <Combine size={12}/>}
                Merge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

